import { useState } from "react";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Loader2, Package, Info, Shield, Tag, FileText, Plus, Mail, Link2, Share2, ChevronDown
} from "lucide-react";

function sanitizeHtml(html) {
  return (html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/on\w+="[^"]*"/gi, "");
}

/**
 * One variant row — full title, size/pack info, click to expand
 * for the full parent description + at-a-glance attributes.
 */
function VariantRow({ variant, parent, onAdd }) {
  const [open, setOpen] = useState(false);
  // Variant titles look like "22G x 1 1/2 (Box of 100) / 22G / 1 1/2" — the
  // first segment is the clean combined label, the rest is Shopify option split.
  const niceTitle = (variant.name || "").split(" / ")[0] || variant.name;
  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-3 p-2.5">
        {variant.image
          ? <img src={variant.image} alt="" className="w-12 h-12 object-contain bg-white rounded shrink-0" />
          : <div className="w-12 h-12 bg-white rounded flex items-center justify-center shrink-0"><Package className="w-5 h-5 text-slate-300" /></div>}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-slate-500">{variant.erp}</p>
          <p className="text-sm text-slate-800 leading-snug" title={variant.name}>
            {niceTitle}
          </p>
          {variant.pack?.display && (
            <p
              className="mt-1 text-[10px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5 leading-snug inline-block"
              data-testid={`variant-pack-${variant.erp}`}
              title={variant.pack.display}
            >
              {variant.pack.display}
            </p>
          )}
          {variant.pricing?.case_price && (
            <p className="mt-1 text-sm" data-testid={`variant-price-${variant.erp}`}>
              <span className="font-black text-emerald-700">${Number(variant.pricing.case_price).toFixed(2)}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 ml-1">/ case</span>
              {variant.pricing.unit_price && (
                <span className="text-[10px] text-slate-500 ml-2">${Number(variant.pricing.unit_price).toFixed(4)}/unit</span>
              )}
            </p>
          )}
          {variant.available === false && (
            <p className="text-[10px] text-rose-500 font-semibold mt-0.5">Back-ordered</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <button
            onClick={() => onAdd(variant)}
            className="text-xs bg-amber-600 hover:bg-amber-700 text-white font-semibold px-2.5 py-1 rounded"
            data-testid={`variant-add-${variant.erp}`}>
            + Add
          </button>
          <button
            onClick={() => setOpen(v => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-0.5"
            data-testid={`variant-toggle-${variant.erp}`}>
            Details
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 pt-1 bg-white border-t border-slate-200">
          <p className="text-sm font-semibold text-slate-800">{parent?.name}</p>
          {variant.name && variant.name !== niceTitle && (
            <p className="text-xs text-slate-500 mt-0.5">Option: {variant.name}</p>
          )}
          {parent?.brand && (
            <p className="text-xs text-cyan-700 mt-1">Brand: {parent.brand}</p>
          )}
          {parent?.description && (
            <p className="text-xs text-slate-700 mt-2 leading-relaxed">
              {parent.description}
            </p>
          )}
          {parent?.attributes && parent.attributes.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5 mt-2">
              {parent.attributes.map((a, i) => (
                <div key={i} className="bg-slate-50 rounded px-2 py-1 border border-slate-200">
                  <p className="text-[9px] text-slate-500 uppercase tracking-wide">{a.name}</p>
                  <p className="text-[11px] font-medium text-slate-800">
                    {(a.values || []).join(", ") || "—"}
                  </p>
                </div>
              ))}
            </div>
          )}
          {parent?.html_content && !parent?.description && (
            <div
              className="prose prose-xs prose-slate max-w-none mt-2 text-xs"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(parent.html_content).slice(0, 600) }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductDetailDialog({
  product, loading, onClose, source = "surgical", shareUrlBuilder
}) {
  const { addItem } = useQuoteCart();

  const defaultShareUrl = (p) => {
    const base = window.location.origin + window.location.pathname;
    return `${base}?product=${encodeURIComponent(p.id || p.erp)}`;
  };
  const makeUrl = shareUrlBuilder || defaultShareUrl;

  const shareViaEmail = (p) => {
    const url = makeUrl(p);
    const subject = `Sterile Mate — ${p.name}${p.erp ? ` (SKU: ${p.erp})` : ""}`;
    const body = [
      `Take a look at this product from the Sterile Mate Marketplace:`,
      ``,
      p.name,
      p.erp ? `SKU: ${p.erp}` : "",
      p.brand ? `Brand: ${p.brand}` : "",
      ``,
      p.description || "",
      ``,
      `View full details & request a quote:`,
      url,
      ``,
      `— Sterile Mate · 281.456.3458 · orders@sterilemate.com`,
    ].filter(Boolean).join("\n");
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copyLink = async (p) => {
    try {
      await navigator.clipboard.writeText(makeUrl(p));
      toast.success("Link copied to clipboard");
    } catch {
      toast.message("Share link", { description: makeUrl(p) });
    }
  };

  const nativeShare = async (p) => {
    if (navigator.share) {
      try {
        await navigator.share({ title: p.name, text: p.description || p.name, url: makeUrl(p) });
      } catch { /* cancelled */ }
    } else copyLink(p);
  };

  const isSolM = (source || "").toLowerCase().includes("sol");

  const doAdd = (item) => addItem({
    erp: item.erp,
    name: item.name,
    image: item.image,
    source: isSolM ? "solm" : source,
    is_solm: isSolM || !!item.is_solm,
    pack: item.pack || null,
    unit_price: item.unit_price ?? (item.pricing?.case_price || item.pricing?.unit_price || 0),
    category: product?.category?.sub,
  });

  return (
    <Dialog open={!!product} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-dialog">
        {loading || (product && product._loading) ? (
          <div className="py-20 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading product details...
          </div>
        ) : product && (
          <>
            <DialogHeader>
              <div className="flex items-start gap-2 mb-1">
                {product.brand && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-0.5">
                    <Tag className="w-3 h-3" /> {product.brand}
                  </span>
                )}
                {product.category && (
                  <span className="text-xs text-slate-500">
                    {product.category.top} / {product.category.sub}
                  </span>
                )}
              </div>
              <DialogTitle className="text-2xl" data-testid="product-detail-title">
                {product.name}
              </DialogTitle>
              <p className="text-sm font-mono text-slate-500">SKU: {product.erp}</p>
            </DialogHeader>

            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                {product.images && product.images[0] ? (
                  <div className="bg-white rounded-xl p-6 border border-slate-200 flex items-center justify-center min-h-[260px]">
                    <img src={product.images[0].large || product.images[0].medium}
                      alt={product.name} className="max-w-full max-h-[340px] object-contain" />
                  </div>
                ) : (
                  <div className="bg-slate-100 rounded-xl p-6 flex items-center justify-center min-h-[260px]">
                    <Package className="w-16 h-16 text-slate-300" />
                  </div>
                )}
                {product.images && product.images.length > 1 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto">
                    {product.images.map((img, i) => (
                      <img key={i} src={img.small || img.medium} alt=""
                        className="w-16 h-16 object-contain bg-white rounded border border-slate-200 flex-shrink-0" />
                    ))}
                  </div>
                )}
              </div>

              <div>
                {product.description && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" /> Description
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">{product.description}</p>
                  </div>
                )}

                {product.attributes && product.attributes.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5" /> At a Glance
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {product.attributes.map((a, i) => (
                        <div key={i} className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wide">{a.name}</p>
                          <p className="text-sm font-medium text-slate-800">
                            {(a.values || []).join(", ") || "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {product.unit_of_measure && (
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold">Unit of Measure:</span> {product.unit_of_measure}
                    {product.pack_description && ` · ${product.pack_description}`}
                  </p>
                )}
              </div>
            </div>

            {/* Specifications (Aspen) */}
            {product.specifications && product.specifications.length > 0 && (
              <div className="mt-6 border-t pt-6">
                {product.specifications.map((spec, i) => (
                  <div key={i} className="mb-5">
                    <h4 className="font-bold text-slate-800 mb-2">{spec.name}</h4>
                    {spec.html ? (
                      <div className="prose prose-sm prose-slate max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(spec.html) }} />
                    ) : spec.value ? <p className="text-sm text-slate-700">{spec.value}</p> : null}
                  </div>
                ))}
              </div>
            )}

            {/* HTML body (Sol-M) */}
            {!product.specifications?.length && product.html_content && (
              <div className="mt-6 border-t pt-6">
                <h4 className="font-bold text-slate-800 mb-2">Details</h4>
                <div className="prose prose-sm prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.html_content) }} />
              </div>
            )}

            {/* Variants */}
            {product.variants && product.variants.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h4 className="font-bold text-slate-800 mb-1">
                  Available Sizes / SKUs ({product.variants.length})
                </h4>
                <p className="text-xs text-slate-500 mb-3">
                  Click <span className="font-semibold">Details</span> on any size to see the full description before adding to your quote.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {product.variants.map((v) => (
                    <VariantRow
                      key={v.erp}
                      variant={v}
                      parent={product}
                      onAdd={(variant) => doAdd({
                        erp: variant.erp,
                        name: `${product.name} — ${variant.name}`,
                        image: variant.image,
                        pack: variant.pack || null,
                        pricing: variant.pricing || null,
                        is_solm: !!(variant.sold_by === "case" || variant.pack),
                      })}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {product.documents && product.documents.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h4 className="font-bold text-slate-800 mb-3">Documents</h4>
                <div className="flex flex-wrap gap-2">
                  {product.documents.map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-100">
                      <FileText className="w-3.5 h-3.5" /> {d.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 pt-6 border-t flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => doAdd({
                    erp: product.erp, name: product.name,
                    image: product.images?.[0]?.medium,
                  })}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="detail-add-to-quote">
                  <Plus className="w-4 h-4 mr-2" /> Add to Quote
                </Button>
                <Button onClick={() => shareViaEmail(product)} variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="detail-email-share-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email Product
                </Button>
                <Button onClick={() => copyLink(product)} variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="detail-copy-link-btn">
                  <Link2 className="w-4 h-4 mr-2" /> Copy Link
                </Button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <Button onClick={() => nativeShare(product)} variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    data-testid="detail-native-share-btn">
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </Button>
                )}
              </div>
              <Button onClick={onClose} variant="outline">Close</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
